import { useMemo, useRef } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { SettingsBar } from '@ardoq/settings-bar';
import { ConnectedTreemapLayout as TreemapLayout } from './Components/TreemapLayout';
import { ExportContainer, ViewWrapper } from './Components/atoms';
import { getViewModel$ } from './viewModel$';
import { ViewModel } from './types';
import { getLeftMenu } from './leftMenu';
import { getRightMenuConfig } from 'viewSettings/getRightMenuConfig';
import { getExportSettings } from 'viewSettings/getExportSettings';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { getAdditionalRowsForGraphViewLegend } from 'tabview/graphViews/graphViewLegend/graphViewLegendUtils';
import { ViewLegendContainer } from '@ardoq/graph';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { isPresentationMode } from 'env';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { EmptyState as EmptyStateBase } from './Components/EmptyState';
import { treemapCommands } from './commands';
import {
  getActiveConditionalFormattingForLegend,
  ViewLegend,
} from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.TREEMAP;

function TreeMapView({
  data,
  settings,
  legend,
  workspaceFields,
  fieldsInUse,
  hasIncomingReferences,
  hasOutgoingReferences,
  hasComponentsAvailable,
  activeFieldLabel,
  permissionContext,
  i18nLocale,
}: ViewModel) {
  const containerElement = useRef<HTMLDivElement>(null);

  const isEmptyView = !data.children?.length;

  const viewLegendLabels = useMemo(
    () => getViewLegendLabels(i18nLocale),
    [i18nLocale]
  );

  const activeConditionalFormattingForLegend =
    getActiveConditionalFormattingForLegend(i18nLocale);

  const additionalRows = getAdditionalRowsForGraphViewLegend({
    componentTypes: legend.componentTypes,
    referenceTypes: [],
    hasCollapsedNodes: false,
    hasReferenceParents: false,
    hasNonComponentNodes: false,
    isUserDefinedGrouping: false,
    showComponentSwatches: false,
    activeConditionalFormattingForLegend: {
      components: activeConditionalFormattingForLegend.components,
      tags: activeConditionalFormattingForLegend.tags,
      references: [],
    },
    showComponentShapes: false,
    showReferencesAsLines: false,
    selectableItems: false,
    isNewConditionalFormatting: false,
    i18nLocale,
  });

  return (
    <>
      {!isPresentationMode() && (
        <SettingsBar
          viewId={VIEW_ID}
          i18nLocale={i18nLocale}
          leftMenu={getLeftMenu({
            settings,
            fields: workspaceFields,
            fieldsInUse,
            hasIncomingReferences,
            hasOutgoingReferences,
            permissionContext,
            i18nLocale,
          })}
          rightMenu={getRightMenuConfig({
            isViewpointMode: false,
            viewId: VIEW_ID,
            viewstate: settings,
            exports: getExportSettings(VIEW_ID, containerElement, i18nLocale),
            knowledgeBaseLink: KnowledgeBaseLink.TREEMAP,
            isLegendDisabled: isEmptyView,
            isExportsDisabled: isEmptyView,
            onViewSettingsUpdate,
            i18nLocale,
          })}
        />
      )}
      <ExportContainer
        ref={containerElement}
        onClick={() => treemapCommands.setFocusedItemState(null)}
        data-context-menu={OBJECT_CONTEXT_MENU_NAME}
      >
        <ViewWrapper $hasData={!isEmptyView}>
          {isEmptyView ? (
            <EmptyStateBase
              hasComponentsAvailable={hasComponentsAvailable}
              sizeBasedOn={settings.sizeBasedOn}
              hasIncomingReferences={hasIncomingReferences}
              hasOutgoingReferences={hasOutgoingReferences}
              fieldsInUse={fieldsInUse}
              workspaceFields={workspaceFields}
              activeFieldLabel={activeFieldLabel}
            />
          ) : (
            <TreemapLayout data={data} />
          )}
        </ViewWrapper>

        <ViewLegendContainer
          heightOffset={useViewLegendSubscription()}
          visible={legend.isActive}
        >
          <ViewLegend
            componentTypes={[]}
            referenceTypes={[]}
            additionalRows={additionalRows}
            activeDiffMode={null}
            labels={viewLegendLabels}
          />
        </ViewLegendContainer>
      </ExportContainer>
    </>
  );
}

const ConnectedTreeMap = connectInstanceWithProps(TreeMapView, getViewModel$);

export { ConnectedTreeMap };
